/**
 * The intentionally small contract shared by the companion tool, the main
 * thread, and the analysis Worker. Artifact locations are kept out of this
 * request: app code resolves them from the authenticated execution context.
 */

export const ANALYSIS_ARTIFACTS = ['debug', 'transactions'] as const
export type AnalysisArtifact = (typeof ANALYSIS_ARTIFACTS)[number]

export const ANALYSIS_OPERATIONS = [
  'schema',
  'sample',
  'signal_summary',
  'trade_summary',
  'signal_window',
] as const
export type AnalysisOperation = (typeof ANALYSIS_OPERATIONS)[number]

export const ANALYSIS_LIMITS = {
  maxInputBytes: 16 * 1024 * 1024,
  maxScannedRows: 100_000,
  maxReturnedRows: 200,
  maxOutputBytes: 128 * 1024,
  maxWorkerMs: 5_000,
  /** Wall-clock budget for resolving app-owned artifact bytes before the Worker starts. */
  maxAcquisitionMs: 15_000,
  /** Metadata-based guard against highly compressed artifacts expanding in the Worker. */
  maxDecodedBytes: 64 * 1024 * 1024,
} as const

export interface AnalysisSelection {
  /** Physical parquet column names. Unknown names fail closed. */
  columns?: string[]
  /** Zero-based, end-exclusive row range. */
  rowStart?: number
  rowEnd?: number
  /** Maximum rows returned by sample/window operations. */
  limit?: number
  symbol?: string
  timeframe?: string
  startTime?: string
  endTime?: string
  /** Required by signal_window; never interpreted as code. */
  signalColumn?: string
  fillTimestampColumn?: string
  beforeMs?: number
  afterMs?: number
  /** Artifact timestamps are interpreted as UTC epoch values; other zones are unsupported. */
  timezone?: 'UTC'
}

export interface AnalyzeBacktestRequest {
  executionId: string
  strategyId?: string | null
  artifact: AnalysisArtifact
  operation: AnalysisOperation
  selection?: AnalysisSelection
}

export type AnalysisRequest = AnalyzeBacktestRequest

/** Trusted app-owned location. Model input must never be used to construct it. */
export interface TrustedArtifactSource {
  executionId: string
  strategyId?: string | null
  artifact: AnalysisArtifact
  bytes?: ArrayBuffer | Uint8Array
  url?: string
  /** signal_window joins a debug source to this trusted transaction artifact. */
  related?: TrustedArtifactLocation
}

export interface TrustedArtifactLocation {
  executionId: string
  strategyId?: string | null
  artifact: AnalysisArtifact
  bytes?: ArrayBuffer | Uint8Array
  url?: string
}

export interface AnalysisCounts {
  scanned: number
  matched: number
  returned: number
}

export interface AnalysisCompleteness {
  /** True only when the requested aggregate/range was fully read. */
  complete: boolean
  truncated: boolean
  /** A bounded list can be truncated while its separate aggregate is complete. */
  aggregateComplete?: boolean
  reason?:
    | 'sample'
    | 'returned_row_limit'
    | 'input_byte_limit'
    | 'decoded_byte_limit'
    | 'output_byte_limit'
    | 'worker_timeout'
    | 'row_limit'
    | 'selection'
}

export interface AnalysisEnvelopeBase {
  executionId: string
  strategyId?: string | null
  artifact: AnalysisArtifact
  operation: AnalysisOperation
  counts: AnalysisCounts
  completeness: AnalysisCompleteness
}

export interface AnalysisColumn {
  name: string
  physicalType?: string
  logicalType?: string
  nullable: boolean
}

export interface SchemaAnalysisData {
  columns: AnalysisColumn[]
  rowCount: number
}

export interface SampleAnalysisData {
  rows: Record<string, unknown>[]
}

export interface BooleanSignalSummary {
  column: string
  kind: 'boolean'
  trueCount: number
  falseCount: number
  nullCount: number
}

export interface NumericSignalSummary {
  column: string
  kind: 'number'
  count: number
  nullCount: number
  min: number | null
  max: number | null
  mean: number | null
  sum: number | null
}

export interface SignalSummaryAnalysisData {
  signals: (BooleanSignalSummary | NumericSignalSummary)[]
}

export interface TradeSummaryAnalysisData {
  fills: {
    count: number
    quantity: number
    proceeds: number
  }
  roundTrips: {
    count: number
    wins: number
    losses: number
    pnl: number
    sample: unknown[]
  }
  costs: {
    commission_paid: number | null
    locate_fees_paid: number | null
    slippage_cost_total: number | null
  }
}

export interface SignalWindowAnalysisData {
  signalColumn: string
  fillTimestampColumn: string
  windows: {
    signalAt: string
    matches: Record<string, unknown>[]
  }[]
}

export type AnalysisData =
  | SchemaAnalysisData
  | SampleAnalysisData
  | SignalSummaryAnalysisData
  | TradeSummaryAnalysisData
  | SignalWindowAnalysisData

export interface AnalysisSuccess extends AnalysisEnvelopeBase {
  status: 'ok'
  ok: true
  data: AnalysisData
}

export interface AnalysisUnavailable extends AnalysisEnvelopeBase {
  status: 'unavailable'
  ok: false
  reason:
    | 'missing_artifact'
    | 'missing_signal_column'
    | 'missing_fill_timestamp'
    | 'artifact_kind'
    | 'expired_location'
  message: string
}

export interface AnalysisLimit extends AnalysisEnvelopeBase {
  status: 'limit'
  ok: false
  limit: keyof typeof ANALYSIS_LIMITS | 'invalid_selection'
  message: string
  suggestion: string
}

export interface AnalysisError extends AnalysisEnvelopeBase {
  status: 'error'
  ok: false
  code: 'invalid_request' | 'decode_failed' | 'cancelled'
  message: string
}

export type AnalysisResponse = AnalysisSuccess | AnalysisUnavailable | AnalysisLimit | AnalysisError

export type AnalysisResult = AnalysisResponse

export type AnalysisWorkerMessage = {
  type: 'analyze'
  request: AnalyzeBacktestRequest
  bytes: ArrayBuffer
  relatedBytes?: ArrayBuffer
}

export type AnalysisWorkerResult = {
  type: 'result'
  response: AnalysisResponse
}
